/**
 * WaitingQueueRow — one row of the pinned pane's "waiting on a person" queue.
 *
 * Shared by ora and the Outlook add-in (`@product-app/common/workflows/…`);
 * there is no forked copy under the add-in tree (design Constraint 20).
 *
 * The row is a single focusable control inside a semantic list item, so the
 * whole queue is operable by keyboard with no custom key handling. Every
 * indicator it carries — item type, group membership (via its header), the
 * elapsed wait, the run's stage — is TEXT. Nothing is signalled by colour or
 * position alone (Accessibility DoD).
 *
 * What the row shows: client · policy, the ELAPSED wait, and — when the
 * server reports it — the run's PROGRESS ("Stage 2 of 4" and a thin bar filled
 * by milestones done). The question itself is not on the row (story 28633);
 * opening the row shows it. There is still no remaining-time or ETA element
 * anywhere in this component, and `formatElapsedWait` cannot produce one.
 *
 * Text uses the pane's three styles only (`paneTypography`).
 */

import React from "react";
import { formatElapsedWait } from "../formatTime";
import { PRIMARY_ACTION_BUTTON_CLASS, TYPE_BODY, TYPE_LABEL, TYPE_TITLE } from "../paneTypography";
import type { WaitingQueueItem } from "../types";

/** Human label for the row's `item_type`. "run" is the only member today —
 *  the field is a declared extension point so a prepared brief can join the
 *  queue later without reshaping the row. */
export function itemTypeLabel(itemType: string | null | undefined): string {
  switch ((itemType ?? "").toLowerCase()) {
    case "run":
      return "Run";
    case "brief":
      return "Brief";
    default:
      return itemType && itemType.trim().length > 0 ? itemType : "Item";
  }
}

/** "Acme Holdings · POL-123", with either half omitted when absent. */
export function subjectLine(item: WaitingQueueItem): string {
  const parts = [item.client_name, item.policy_number].filter(
    (p): p is string => typeof p === "string" && p.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(" · ") : "Client not identified";
}

/** The row's progress, or null when the server did not report ALL of it. */
export interface QueueRowProgress {
  stageIndex: number;
  stageTotal: number;
  milestonesDone: number;
  milestoneTotal: number;
  /** "Stage 2 of 4" — the visible text and the bar's accessible name. */
  stageText: string;
  /** "3 of 5 milestones done" — what the bar's fill means, as words. */
  milestoneText: string;
  /** Fill, 0..1. */
  fraction: number;
}

function isCount(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

/**
 * Story 28633 overrides story 26739's design decision D4 ("no counters") for
 * THIS queue only: an AM choosing which parked run to open next is helped by
 * seeing how far along each one is. Every other surface keeps D4. All four
 * fields must be present and both totals positive — a partial report renders
 * no progress rather than a guessed one.
 */
export function queueRowProgress(item: WaitingQueueItem): QueueRowProgress | null {
  const { stage_index, stage_total, milestones_done, milestone_total } = item;
  if (
    !isCount(stage_index) ||
    !isCount(stage_total) ||
    !isCount(milestones_done) ||
    !isCount(milestone_total) ||
    stage_total === 0 ||
    milestone_total === 0
  ) {
    return null;
  }
  return {
    stageIndex: stage_index,
    stageTotal: stage_total,
    milestonesDone: milestones_done,
    milestoneTotal: milestone_total,
    stageText: `Stage ${stage_index} of ${stage_total}`,
    milestoneText: `${milestones_done} of ${milestone_total} milestones done`,
    fraction: Math.min(1, Math.max(0, milestones_done / milestone_total)),
  };
}

function elapsedTone(
  sinceIso: string | null | undefined,
  now: Date,
): { color: string; bg: string; border: string } {
  if (!sinceIso) {
    return {
      color: "text-primary-gray-light-500",
      bg: "bg-primary-gray-light-50",
      border: "border-primary-gray-light-200",
    };
  }
  const ms = now.getTime() - new Date(sinceIso).getTime();
  if (Number.isNaN(ms) || ms < 0) {
    return {
      color: "text-primary-gray-light-500",
      bg: "bg-primary-gray-light-50",
      border: "border-primary-gray-light-200",
    };
  }
  if (ms >= 45 * 60 * 1000) {
    return {
      color: "text-primary-error-700",
      bg: "bg-primary-error-50",
      border: "border-primary-error-200",
    };
  }
  if (ms >= 15 * 60 * 1000) {
    return {
      color: "text-primary-warning-700",
      bg: "bg-primary-warning-50",
      border: "border-primary-warning-200",
    };
  }
  return {
    color: "text-primary-gray-light-500",
    bg: "bg-primary-gray-light-50",
    border: "border-primary-gray-light-200",
  };
}

export interface WaitingQueueRowProps {
  item: WaitingQueueItem;
  /** Raised to the host — the row never navigates by itself. */
  onOpenItem: (instanceId: string) => void;
  /** Injected only so tests and the render probe can pin "now". */
  now?: Date;
  /** First needs-you tile: yellow frame + Do this next bar. */
  featured?: boolean;
}

export function WaitingQueueRow({
  item,
  onOpenItem,
  now,
  featured = false,
}: WaitingQueueRowProps): React.ReactElement {
  const clock = now ?? new Date();
  const elapsed = formatElapsedWait(item.waiting_since, clock);
  const type = itemTypeLabel(item.item_type);
  const client =
    typeof item.client_name === "string" && item.client_name.trim()
      ? item.client_name.trim()
      : "Client not identified";
  const policy =
    typeof item.policy_number === "string" && item.policy_number.trim()
      ? item.policy_number.trim()
      : null;
  const tone = elapsedTone(item.waiting_since, clock);
  const progress = queueRowProgress(item);

  // The accessible name carries the SPELLED-OUT wait ("waiting 45 minutes"),
  // not just the "45m" badge — the badge alone is a visual shorthand. The
  // progress is spelled out too: a button's children are presentational, so
  // the bar inside it is not announced on its own.
  const progressName = progress
    ? ` ${progress.stageText}, ${progress.milestoneText}.`
    : "";
  const accessibleName = `${type}. ${subjectLine(item)}.${progressName} ${elapsed.aria}.${
    featured ? " Action needed." : ""
  }`;

  return (
    <li className="list-none">
      <button
        type="button"
        onClick={() => onOpenItem(item.instance_id)}
        aria-label={accessibleName}
        data-testid="waiting-queue-row"
        data-instance-id={item.instance_id}
        data-needs-you={item.needs_you ? "true" : "false"}
        data-featured={featured ? "true" : "false"}
        className={
          "flex w-full flex-col text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-sunrise " +
          (featured
            ? "rounded-lg border border-sunrise bg-[#FFFDF0] p-3"
            : "rounded-lg border border-primary-gray-light-200 bg-base-white p-3 hover:border-primary-gray-light-300")
        }
      >
        <span className="flex items-start gap-2.5">
          <span className="flex min-w-0 flex-1 flex-col gap-1.5">
            <span className="flex flex-wrap items-baseline gap-x-[7px]">
              <span className={`${TYPE_TITLE} text-primary-gray-light-900`}>
                {client}
              </span>
              {policy ? (
                <span className={`${TYPE_BODY} text-primary-gray-light-500`}>
                  · {policy}
                </span>
              ) : null}
            </span>
            {progress ? (
              <span
                className="flex items-center gap-2"
                data-testid="waiting-queue-row-progress"
              >
                <span
                  className={`shrink-0 ${TYPE_LABEL} text-primary-gray-light-500`}
                >
                  {progress.stageText}
                </span>
                <span
                  role="progressbar"
                  aria-label={progress.stageText}
                  aria-valuemin={0}
                  aria-valuemax={progress.milestoneTotal}
                  aria-valuenow={Math.min(progress.milestonesDone, progress.milestoneTotal)}
                  aria-valuetext={progress.milestoneText}
                  data-testid="waiting-queue-row-progress-bar"
                  className="block h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-primary-gray-light-200"
                >
                  <span
                    className="block h-full rounded-full bg-sunrise"
                    style={{ width: `${Math.round(progress.fraction * 100)}%` }}
                  />
                </span>
              </span>
            ) : null}
          </span>
          <span
            aria-hidden="true"
            data-testid="waiting-queue-row-elapsed"
            className={
              `inline-flex shrink-0 items-center gap-1 rounded-[10px] border px-[9px] py-[3px] ${TYPE_LABEL} normal-case ` +
              `${tone.color} ${tone.bg} ${tone.border}`
            }
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              className="flex-shrink-0"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
            {elapsed.label}
          </span>
        </span>
        {featured ? (
          // Review & Approve's exact look (PRIMARY_ACTION_BUTTON_CLASS) and
          // markup: the label, then an aria-hidden arrow.
          <span
            data-testid="waiting-queue-row-action"
            className={`mt-2 ${PRIMARY_ACTION_BUTTON_CLASS}`}
          >
            Do this next
            <span aria-hidden="true">→</span>
          </span>
        ) : null}
      </button>
    </li>
  );
}

export default WaitingQueueRow;
